// components/radiologists/Radiologists.jsx

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Stack,
  Paper,
  Chip,
  TextField,
  Autocomplete,
  Menu,
  Divider,
  CircularProgress,
  TablePagination,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  FilterList,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  Wc as GenderIcon,
  Business as BranchIcon,
  CheckCircle as StatusIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';

import NoPrivilege from '../../components/common/NoPrivilege';
import NoContent from '../../components/common/NoContent';
import SearchBar from '../../components/common/SearchBar';
import { useRadiologists } from './hooks/useRadiolgists.js';
import { useRTL } from '../../hooks/useRTL';
import RadiologistCard from './components/radiologistCard';
import RadiologistFormDialog from './components/radiologistFormDialog';
import DeleteConfirmDialog from './components/deleteConfirmDialog.jsx';

// Export format options
const EXPORT_FORMATS = {
  simple: {
    key: 'simple',
    labelKey: 'radiologists.export.simple',
    defaultLabel: 'Simple Export',
    descriptionKey: 'radiologists.export.simpleDesc',
    defaultDescription: 'Basic radiologist information',
    icon: <DescriptionIcon fontSize="small" />
  },
  detailed: {
    key: 'detailed',
    labelKey: 'radiologists.export.detailed',
    defaultLabel: 'Detailed Export',
    descriptionKey: 'radiologists.export.detailedDesc',
    defaultDescription: 'Includes specialization and full details',
    icon: <TableChartIcon fontSize="small" />
  },
  byGender: {
    key: 'byGender',
    labelKey: 'radiologists.export.byGender',
    defaultLabel: 'Export by Gender',
    descriptionKey: 'radiologists.export.byGenderDesc',
    defaultDescription: 'Grouped by gender',
    icon: <GenderIcon fontSize="small" />
  },
  byBranch: {
    key: 'byBranch',
    labelKey: 'radiologists.export.byBranch',
    defaultLabel: 'Export by Branch',
    descriptionKey: 'radiologists.export.byBranchDesc',
    defaultDescription: 'Grouped by branch location',
    icon: <BranchIcon fontSize="small" />
  },
  byStatus: {
    key: 'byStatus',
    labelKey: 'radiologists.export.byStatus',
    defaultLabel: 'Export by Status',
    descriptionKey: 'radiologists.export.byStatusDesc',
    defaultDescription: 'Grouped by active/inactive status',
    icon: <StatusIcon fontSize="small" />
  }
};

const Radiologists = () => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();

  // Filter options
  const SORT_BY_OPTIONS = [
    { value: 'name', label: t('common.name', 'Name') },
    { value: 'age', label: t('radiologists.age', 'Age') },
    { value: 'createdAt', label: t('common.createdAt', 'Created At') },
    { value: 'lastLogin', label: t('common.lastLogin', 'Last Login') },
  ];

  const SORT_ORDER_OPTIONS = [
    { value: 'asc', label: t('common.ascending', 'Ascending') },
    { value: 'desc', label: t('common.descending', 'Descending') },
  ];

  const STATUS_OPTIONS = [
    { value: 'all', label: t('common.all', 'All') },
    { value: 'active', label: t('common.active', 'Active') },
    { value: 'inactive', label: t('common.inactive', 'Inactive') },
  ];

  const GENDER_OPTIONS = [
    { value: '', label: t('common.all', 'All') },
    { value: 'male', label: t('common.male', 'Male') },
    { value: 'female', label: t('common.female', 'Female') },
  ];

  const {
    radiologists,
    loading,
    privilegeError,
    openDialog,
    selectedRadiologist,
    deleteConfirmOpen,
    radiologistToDelete,
    permissions,
    pagination,
    filters,
    hasActiveFilters,
    exporting,
    exportMenuAnchor,
    handleOpenDialog,
    handleCloseDialog,
    handleDelete,
    handleDeleteClick,
    setDeleteConfirmOpen,
    handlePageChange,
    handleLimitChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    clearFilters,
    handleExportMenuOpen,
    handleExportMenuClose,
    handleExportSimple,
    handleExportDetailed,
    handleExportByGender,
    handleExportByBranch,
    handleExportByStatus,
    formik
  } = useRadiologists();

  // Export menu state
  const exportMenuOpen = Boolean(exportMenuAnchor);

  // Handle export based on format
  const handleExport = (format) => {
    switch (format) {
      case 'simple':
        handleExportSimple();
        break;
      case 'detailed':
        handleExportDetailed();
        break;
      case 'byGender':
        handleExportByGender();
        break;
      case 'byBranch':
        handleExportByBranch();
        break;
      case 'byStatus':
        handleExportByStatus();
        break;
      default:
        handleExportSimple();
    }
  };

  // Pagination handlers for TablePagination
  const handleTablePageChange = (event, newPage) => {
    handlePageChange(event, newPage + 1); // TablePagination is 0-indexed
  };

  const handleRowsPerPageChange = (event) => {
    handleLimitChange(event);
  };

  // Permission check
  if (!permissions.canView || privilegeError) {
    return (
      <NoPrivilege
        message={t('radiologists.noPrivilegeError', 'You do not have permission to view radiologists.')}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('radiologists.title', 'Radiologists')}
          </Typography>
          {!loading && (
            <Chip
              label={`${pagination.total} ${t('common.total', 'total')}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Export Button with Dropdown */}
          {permissions.canExport && (
            <>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                endIcon={<ArrowDownIcon />}
                onClick={handleExportMenuOpen}
                disabled={exporting || loading || pagination.total === 0}
                aria-controls={exportMenuOpen ? 'radiologist-export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={exportMenuOpen ? 'true' : undefined}
              >
                {exporting
                  ? t('common.exporting', 'Exporting...')
                  : t('common.export', 'Export')
                }
              </Button>
              <Menu
                id="radiologist-export-menu"
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'radiologist-export-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {/* Simple Export */}
                <MenuItem onClick={() => handleExport('simple')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.simple.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.simple.labelKey, EXPORT_FORMATS.simple.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.simple.descriptionKey, EXPORT_FORMATS.simple.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Detailed Export */}
                <MenuItem onClick={() => handleExport('detailed')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.detailed.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.detailed.labelKey, EXPORT_FORMATS.detailed.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.detailed.descriptionKey, EXPORT_FORMATS.detailed.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Gender */}
                <MenuItem onClick={() => handleExport('byGender')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byGender.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byGender.labelKey, EXPORT_FORMATS.byGender.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byGender.descriptionKey, EXPORT_FORMATS.byGender.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Branch */}
                <MenuItem onClick={() => handleExport('byBranch')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byBranch.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byBranch.labelKey, EXPORT_FORMATS.byBranch.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byBranch.descriptionKey, EXPORT_FORMATS.byBranch.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Status */}
                <MenuItem onClick={() => handleExport('byStatus')} disabled={exporting}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.byStatus.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.byStatus.labelKey, EXPORT_FORMATS.byStatus.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.byStatus.descriptionKey, EXPORT_FORMATS.byStatus.defaultDescription)}
                  />
                </MenuItem>
              </Menu>
            </>
          )}
          {permissions.canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t('radiologists.addTitle', 'Add Radiologist')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters Paper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
          {hasActiveFilters && (
            <Chip
              label={t('common.filtersApplied', 'Filters will be applied to export')}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Filter Row 1 - Search, Status, Gender */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" mb={2}>
          <SearchBar
            onSearch={(query) => handleSearchChange(query)}
            initialValue={filters.search}
            placeholder={t('radiologists.searchPlaceholder', 'Search radiologists...')}
            sx={{ flexGrow: 1, minWidth: '250px' }}
          />

          {/* Status Filter */}
          <Autocomplete
            options={STATUS_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={STATUS_OPTIONS.find(opt => opt.value === filters.status)}
            onChange={(event, newValue) => handleFilterChange('status', newValue?.value || 'all')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.status', 'Status')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/* Gender Filter */}
          <Autocomplete
            options={GENDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={GENDER_OPTIONS.find(opt => opt.value === filters.gender) || GENDER_OPTIONS[0]}
            onChange={(event, newValue) => handleFilterChange('gender', newValue?.value || '')}
            renderInput={(params) => (
              <TextField {...params} label={t('radiologists.gender', 'Gender')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />
          <Autocomplete
            options={SORT_BY_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
            onChange={(event, newValue) => handleSortChange(newValue?.value || 'name', filters.sortOrder)}
            renderInput={(params) => (
              <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          <Autocomplete
            options={SORT_ORDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
            onChange={(event, newValue) => handleSortChange(filters.sortBy, newValue?.value || 'asc')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.order', 'Order')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />
        </Stack>

        {/* Filter Row 2 - Sort Options */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">


          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="text"
              color="inherit"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              {t('common.clearFilters', 'Clear')}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.search && (
            <Chip
              label={`${t('common.search', 'Search')}: ${filters.search}`}
              size="small"
              onDelete={() => handleSearchChange('')}
            />
          )}
          {filters.status !== 'all' && (
            <Chip
              label={`${t('common.status', 'Status')}: ${STATUS_OPTIONS.find(o => o.value === filters.status)?.label}`}
              size="small"
              color={filters.status === 'active' ? 'success' : 'default'}
              onDelete={() => handleFilterChange('status', 'all')}
            />
          )}
          {filters.gender && (
            <Chip
              label={`${t('radiologists.gender', 'Gender')}: ${GENDER_OPTIONS.find(o => o.value === filters.gender)?.label}`}
              size="small"
              color="secondary"
              onDelete={() => handleFilterChange('gender', '')}
            />
          )}
          {filters.branch && (
            <Chip
              label={`${t('radiologists.branch', 'Branch')}: ${filters.branch}`}
              size="small"
              color="primary"
              onDelete={() => handleFilterChange('branch', '')}
            />
          )}
        </Box>
      )}

      {/* Results Info */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')} {pagination.total === 0 ? 0 : Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}-{Math.min(pagination.page * pagination.limit, pagination.total)} {t('common.of', 'of')} {pagination.total} {t('common.items', 'items')}
      </Typography>

      {/* Content */}
      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Paper>
      ) : radiologists.length === 0 ? (
        <Paper sx={{ p: 4 }}>
          <NoContent
            icon={<PersonIcon sx={{ fontSize: 64 }} />}
            title={
              hasActiveFilters
                ? t('radiologists.noFilterResults', 'No radiologists match your filters')
                : t('radiologists.noData', 'No Radiologists Found')
            }
            description={
              hasActiveFilters
                ? t('radiologists.tryAdjustingFilters', 'Try adjusting your filter criteria')
                : t('radiologists.noDataDescription', 'Start by adding your first radiologist.')
            }
            action={
              permissions.canAdd && !hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  {t('radiologists.addTitle', 'Add Radiologist')}
                </Button>
              )
            }
          />
        </Paper>
      ) : (
        <>
          {/* Radiologist Cards Grid */}
          <Grid
            container
            spacing={3}
            dir={isRTL ? 'rtl' : 'ltr'}
            sx={{
              flexDirection: isRTL ? 'row-reverse' : 'row',
              justifyContent: isRTL ? 'flex-end' : 'flex-start',
            }}
          >
            {radiologists.map((radiologist) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={radiologist._id}>
                <RadiologistCard
                  radiologist={radiologist}
                  canEdit={permissions.canEdit}
                  canDelete={permissions.canDelete}
                  onEdit={() => handleOpenDialog(radiologist)}
                  onDelete={() => handleDeleteClick(radiologist)}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Paper sx={{ mt: 3 }}>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page - 1} // TablePagination is 0-indexed
              onPageChange={handleTablePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[6, 9, 12, 18, 24]}
              labelRowsPerPage={t('common.rowsPerPage', 'Items per page')}
            />
          </Paper>
        </>
      )}

      {/* Dialogs */}
      <RadiologistFormDialog
        open={openDialog}
        onClose={handleCloseDialog}
        selectedRadiologist={selectedRadiologist}
        formik={formik}
        loading={loading}
      />

      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        radiologist={radiologistToDelete}
        onConfirm={() => handleDelete(radiologistToDelete?._id)}
        loading={loading}
      />
    </Box>
  );
};

export default Radiologists;